import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import TaskCard from '@/components/TaskCard';
import AddTaskDialog from '@/components/AddTaskDialog';
import HelpDialog from '@/components/HelpDialog';
import ContextSelector from '@/components/ContextSelector';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, User, Menu } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import { parseLocalDate } from '@/utils/dateHelpers';

export default function StoragePage({ tasks, fetchTasks, tags, dragHandlePosition = 'right' }) {
  const { assigneeTagsEnabled, upcomingDays, horizontalGroups, energyTagsEnabled, storageActiveVisibility } = useSettings();
  const [storageTasks, setStorageTasks] = useState([]);
  const [groupBy, setGroupBy] = useState(() => {
    // Load from localStorage or default to 'Domain'
    return localStorage.getItem('storageGroupBy') || 'Domain';
  });
  const [grouped, setGrouped] = useState({});
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'upcoming', 'overdue'
  const [hideActive, setHideActive] = useState(() => {
    // Load from localStorage or default to false
    return localStorage.getItem('storageHideActive') === 'true';
  });
  const [showAddTask, setShowAddTask] = useState(false);
  const [prefilledTag, setPrefilledTag] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState('any-user');
  const groupsContainerRef = useRef(null);
  const fixedScrollbarRef = useRef(null);
  const [currentVisibleGroup, setCurrentVisibleGroup] = useState(null);
  const [scrollState, setScrollState] = useState({ scrollLeft: 0, scrollWidth: 0, clientWidth: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isInitialMountRef = useRef(true);
  
  // Pagination state: tracks how many tasks to show per group
  const TASKS_PER_GROUP = 10;
  const [visibleTasksPerGroup, setVisibleTasksPerGroup] = useState({});

  // Reset pagination when groupBy changes
  const handleGroupByChange = (newGroupBy) => {
    setGroupBy(newGroupBy);
    setVisibleTasksPerGroup({}); // Reset to show initial 10 per group
    isInitialMountRef.current = true; // Reset scroll position
  };

  // Load more tasks for a specific group (vertical mode) or all groups (horizontal mode)
  const handleLoadMoreForGroup = (groupName) => {
    if (horizontalGroups) {
      // In horizontal mode, load more for ALL groups
      setVisibleTasksPerGroup(prev => {
        const newState = {};
        Object.keys(grouped).forEach(key => {
          newState[key] = (prev[key] || TASKS_PER_GROUP) + TASKS_PER_GROUP;
        });
        return newState;
      });
    } else {
      // In vertical mode, load more for just this group
      setVisibleTasksPerGroup(prev => ({
        ...prev,
        [groupName]: (prev[groupName] || TASKS_PER_GROUP) + TASKS_PER_GROUP
      }));
    }
  };

  // Get the number of visible tasks for a group
  const getVisibleCount = (groupName) => {
    return visibleTasksPerGroup[groupName] || TASKS_PER_GROUP;
  };

  // Reset groupBy to Type if assignee tags are disabled and currently grouped by Assignee
  useEffect(() => {
    if (!assigneeTagsEnabled && groupBy === 'Assignee') {
      handleGroupByChange('Type');
      localStorage.setItem('storageGroupBy', 'Type');
    }
  }, [assigneeTagsEnabled, groupBy]);

  // Save hideActive to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('storageHideActive', hideActive.toString());
  }, [hideActive]);

  // Dynamic padding for horizontal groups container
  const [horizontalPadding, setHorizontalPadding] = useState('1rem');
  const [horizontalRightPadding, setHorizontalRightPadding] = useState('1rem');
  // Dynamic bottom padding for vertical layout to allow last groups to scroll to top
  const [verticalBottomPadding, setVerticalBottomPadding] = useState('0px');
  
  // Scroll to start and calculate proper padding when horizontal mode is active
  useEffect(() => {
    if (horizontalGroups && groupsContainerRef.current) {
      const timer = setTimeout(() => {
        const container = groupsContainerRef.current;
        
        // Only scroll to start on initial mount, not on subsequent data updates
        if (isInitialMountRef.current) {
          container.scrollTo({ left: 0, behavior: 'instant' });
          isInitialMountRef.current = false;
        }
        
        // Calculate the padding needed to align with the header
        const pageTitle = document.querySelector('[data-testid="storage-page"] h1');
        if (pageTitle) {
          const titleRect = pageTitle.getBoundingClientRect();
          setHorizontalPadding(`${titleRect.left}px`);
          alignmentPositionRef.current = titleRect.left;
          
          // Calculate right padding so the last group, at max scroll, stops
          // aligned with the header's left edge (its left edge cannot go past
          // the header's left edge).
          const viewportWidth = window.innerWidth;
          const columnWidth = viewportWidth >= 640 ? 420 : viewportWidth - 80;
          const rightPadding = viewportWidth - columnWidth - titleRect.left;
          setHorizontalRightPadding(`${Math.max(16, rightPadding)}px`);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [horizontalGroups, grouped]);

  // Calculate bottom padding for vertical layout to allow last groups to scroll to header
  useEffect(() => {
    if (horizontalGroups || !groupsContainerRef.current) {
      setVerticalBottomPadding('0px');
      return;
    }
    
    const timer = setTimeout(() => {
      const container = groupsContainerRef.current;
      const groups = container.querySelectorAll('[data-group-id]');
      if (groups.length === 0) return;
      
      // Get viewport height and sticky header height
      const viewportHeight = window.innerHeight;
      const stickyContainer = document.querySelector('[data-testid="storage-page"] > div.sticky');
      const stickyHeight = stickyContainer ? stickyContainer.getBoundingClientRect().height : 120;
      
      // Calculate padding needed so last group can scroll to just below header
      // We want: viewportHeight - stickyHeight - lastGroupHeight - someBuffer
      const lastGroup = groups[groups.length - 1];
      const lastGroupHeight = lastGroup.getBoundingClientRect().height;
      
      // Padding = viewport height - sticky header - last group height - buffer
      const neededPadding = Math.max(0, viewportHeight - stickyHeight - lastGroupHeight - 100);
      setVerticalBottomPadding(`${neededPadding}px`);
    }, 200);
    
    return () => clearTimeout(timer);
  }, [horizontalGroups, grouped]);

  // Track visible group on scroll for mobile "Jump to" dropdown
  useEffect(() => {
    if (!groupsContainerRef.current) return;
    
    const container = groupsContainerRef.current;
    
    const handleScroll = () => {
      const groups = container.querySelectorAll('[data-group-id]');
      if (groups.length === 0) return;
      
      let closestGroup = null;
      
      if (horizontalGroups) {
        // For horizontal layout, find group closest to center
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;
        
        let closestDistance = Infinity;
        
        groups.forEach((group) => {
          const groupRect = group.getBoundingClientRect();
          const groupCenter = groupRect.left + groupRect.width / 2;
          const distance = Math.abs(groupCenter - containerCenter);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestGroup = group.getAttribute('data-group-id');
          }
        });
      } else {
        // For vertical layout, find group closest to top of viewport (below sticky header)
        const stickyContainer = document.querySelector('[data-testid="storage-page"] > div.sticky');
        const stickyBottom = stickyContainer ? stickyContainer.getBoundingClientRect().bottom : 120;
        
        let closestDistance = Infinity;
        
        groups.forEach((group) => {
          const groupRect = group.getBoundingClientRect();
          const distance = Math.abs(groupRect.top - stickyBottom);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestGroup = group.getAttribute('data-group-id');
          }
        });
      }
      
      if (closestGroup) {
        setCurrentVisibleGroup(closestGroup);
      }
    };
    
    // Initial check
    const timer = setTimeout(handleScroll, 200);
    
    if (horizontalGroups) {
      // Listen to container scroll for horizontal
      container.addEventListener('scroll', handleScroll);
      return () => {
        clearTimeout(timer);
        container.removeEventListener('scroll', handleScroll);
      };
    } else {
      // Listen to window scroll for vertical
      window.addEventListener('scroll', handleScroll);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [horizontalGroups, grouped]);

  // Sync scroll between main container and fixed scrollbar (desktop only)
  useEffect(() => {
    if (!horizontalGroups || !groupsContainerRef.current) return;
    
    const container = groupsContainerRef.current;
    
    // Update scroll state when container scrolls
    const updateScrollState = () => {
      setScrollState({
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth
      });
    };
    
    // Initial update
    updateScrollState();
    
    // Listen for scroll and resize events
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    
    // Also update when grouped changes (new content)
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);
    
    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [horizontalGroups, grouped]);

  // Handle fixed scrollbar scroll
  const handleFixedScrollbarScroll = (e) => {
    if (groupsContainerRef.current) {
      groupsContainerRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Store the initial alignment position when groups are first rendered
  const alignmentPositionRef = useRef(null);
  
  // Scroll to a specific group when clicking on a tag in the navigation
  const scrollToGroup = (groupName) => {
    if (!groupsContainerRef.current) return;
    
    const container = groupsContainerRef.current;
    const groupElement = container.querySelector(`[data-group-id="${groupName}"]`);
    
    if (groupElement) {
      if (horizontalGroups) {
        // For horizontal layout, align with the header's left edge
        // Get the current position of the "Task Storage" title
        const pageTitle = document.querySelector('[data-testid="storage-page"] h1');
        if (!pageTitle) return;
        
        const titleRect = pageTitle.getBoundingClientRect();
        const groupRect = groupElement.getBoundingClientRect();
        
        // We want the group's left edge to be at titleRect.left
        const adjustment = groupRect.left - titleRect.left;
        
        const newScrollLeft = container.scrollLeft + adjustment;
        container.scrollTo({ left: Math.max(0, newScrollLeft), behavior: 'smooth' });
      } else {
        // For vertical layout, scroll so the group snaps just below the sticky header (including tag nav)
        // The sticky container includes the header and tag navigation bar
        const stickyContainer = document.querySelector('[data-testid="storage-page"] > div.sticky');
        
        if (stickyContainer) {
          // Get the bottom edge of the sticky container
          const stickyBottom = stickyContainer.getBoundingClientRect().bottom;
          const groupRect = groupElement.getBoundingClientRect();
          
          // Calculate scroll: current scroll + (group top - sticky bottom) - some padding
          const scrollTop = window.scrollY + (groupRect.top - stickyBottom) - 16;
          window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
        } else {
          // Fallback to scrollIntoView
          groupElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  // Get tags for navigation based on current groupBy
  const getNavigationTags = () => {
    if (groupBy === 'Active Date' || groupBy === 'Due Date') {
      return []; // No tag navigation for date groupings
    }
    
    const categoryToMatch = groupBy === 'Assignee' ? 'assignee' : groupBy;
    return tags
      .filter((t) => t.category === categoryToMatch)
      .sort((a, b) => a.priority - b.priority);
  };

  const navigationTags = getNavigationTags();

  // Helper function to format date bucket keys to display names
  const formatDateHeader = (bucketKey) => {
    if (bucketKey === 'today') return 'Today';
    if (bucketKey === 'tomorrow') return 'Tomorrow';
    if (bucketKey === 'yesterday') return 'Yesterday';
    
    if (bucketKey.startsWith('future-day-')) {
      const days = parseInt(bucketKey.split('-')[2]);
      return `${days} days from now`;
    }
    
    if (bucketKey.startsWith('future-week-')) {
      const weeks = parseInt(bucketKey.split('-')[2]);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} from now`;
    }
    
    if (bucketKey.startsWith('past-day-')) {
      const days = parseInt(bucketKey.split('-')[2]);
      return `${days} days ago`;
    }
    
    if (bucketKey.startsWith('past-week-')) {
      const weeks = parseInt(bucketKey.split('-')[2]);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    }
    
    return bucketKey; // Fallback
  };

  // Save groupBy to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('storageGroupBy', groupBy);
  }, [groupBy]);

  // Get assignee tags for the filter dropdown
  const assigneeTags = tags.filter((t) => t.category === 'assignee').sort((a, b) => a.priority - b.priority);

  // Build dynamic priority maps from tags
  const typePriorityMap = {};
  const domainPriorityMap = {};
  const energyPriorityMap = {};
  const assigneePriorityMap = {};
  
  tags.forEach((tag) => {
    const tagKey = `${tag.emoji} ${tag.name}`;
    if (tag.category === 'Type') {
      typePriorityMap[tagKey] = tag.priority;
    } else if (tag.category === 'Domain') {
      domainPriorityMap[tagKey] = tag.priority;
    } else if (tag.category === 'Energy') {
      energyPriorityMap[tagKey] = tag.priority;
    } else if (tag.category === 'assignee') {
      assigneePriorityMap[tagKey] = tag.priority;
    }
  });

  // Determine if active tasks should be hidden (either from setting or button)
  const shouldHideActive = storageActiveVisibility === 'hidden' || hideActive;

  useEffect(() => {
    // Filter incomplete tasks
    let filtered = tasks.filter((t) => !t.completed);
    
    // Apply "Hide Active" filter (from setting or button)
    if (shouldHideActive) {
      filtered = filtered.filter((t) => !t.is_active);
    }
    
    // Apply assignee filter - include "Any User" tasks when a specific assignee is selected
    if (assigneeTagsEnabled && assigneeFilter && assigneeFilter !== 'any-user') {
      filtered = filtered.filter(task => task.assignee_tag === assigneeFilter);
    }
    
    // Apply date filters
    const now = new Date();
    const upcomingDaysFromNow = new Date(now.getTime() + upcomingDays * 24 * 60 * 60 * 1000);
    
    if (filterMode === 'upcoming') {
      filtered = filtered.filter((t) => {
        if (!t.due_date) return false;
        const dueDate = parseLocalDate(t.due_date);
        return dueDate > now && dueDate <= upcomingDaysFromNow;
      });
    } else if (filterMode === 'overdue') {
      filtered = filtered.filter((t) => {
        if (!t.due_date) return false;
        const dueDate = parseLocalDate(t.due_date);
        return dueDate < now;
      });
    }
    
    setStorageTasks(filtered);
  }, [tasks, filterMode, shouldHideActive, upcomingDays, assigneeFilter, assigneeTagsEnabled]);

  // Calculate counts for filter buttons
  const now = new Date();
  const upcomingDaysFromNow = new Date(now.getTime() + upcomingDays * 24 * 60 * 60 * 1000);
  const upcomingCount = tasks.filter((t) => {
    if (t.completed || !t.due_date) return false;
    const dueDate = parseLocalDate(t.due_date);
    return dueDate > now && dueDate <= upcomingDaysFromNow;
  }).length;
  const overdueCount = tasks.filter((t) => {
    if (t.completed || !t.due_date) return false;
    const dueDate = parseLocalDate(t.due_date);
    return dueDate < now;
  }).length;

  useEffect(() => {
    // Group tasks
    const groups = {};
    
    // Helper to normalize date to a grouping key
    const normalizeDateKey = (dateStr) => {
      const date = parseLocalDate(dateStr);
      if (!date) return null;
      
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Group by relative time buckets
      if (diffDays === 0) {
        return 'today';
      } else if (diffDays === 1) {
        return 'tomorrow';
      } else if (diffDays === -1) {
        return 'yesterday';
      } else if (diffDays > 0) {
        // Future dates
        if (diffDays >= 7) {
          // Group by weeks (7-13 days = week 1, 14-20 days = week 2, etc.)
          const weeks = Math.floor((diffDays + 3.5) / 7);
          return `future-week-${weeks}`;
        } else {
          // 2-6 days: individual days
          return `future-day-${diffDays}`;
        }
      } else {
        // Past dates
        const absDays = Math.abs(diffDays);
        if (absDays >= 7) {
          // Group by weeks
          const weeks = Math.floor((absDays + 3.5) / 7);
          return `past-week-${weeks}`;
        } else {
          // 2-6 days ago: individual days
          return `past-day-${absDays}`;
        }
      }
    };
    
    storageTasks.forEach((task) => {
      let key = 'Untagged';
      
      if (groupBy === 'Active Date') {
        // Group by activation_date
        // First check if task is currently active
        if (task.is_active && !task.completed) {
          key = 'Currently Active';
        } else if (task.activation_date) {
          key = normalizeDateKey(task.activation_date) || 'No Date';
        } else {
          key = 'No Date';
        }
      } else if (groupBy === 'Due Date') {
        // Group by due_date, normalize to YYYY-MM-DD
        if (task.due_date) {
          key = normalizeDateKey(task.due_date) || 'No Date';
        } else {
          key = 'No Date';
        }
      } else {
        // Group by tags (Type, Domain, Energy, Assignee)
        if (groupBy === 'Type' && task.tag_type) {
          key = task.tag_type;
        } else if (groupBy === 'Domain' && task.tag_domain) {
          key = task.tag_domain;
        } else if (groupBy === 'Energy' && task.tag_energy) {
          key = task.tag_energy;
        } else if (groupBy === 'Assignee' && task.assignee_tag) {
          key = task.assignee_tag;
        }
      }
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });
    
    // Sort within groups
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        // Parse dates
        const activeDateA = a.activation_date ? parseLocalDate(a.activation_date) : null;
        const activeDateB = b.activation_date ? parseLocalDate(b.activation_date) : null;
        const dueDateA = a.due_date ? parseLocalDate(a.due_date) : null;
        const dueDateB = b.due_date ? parseLocalDate(b.due_date) : null;
        
        // Check if task is "Active: Now" (is_active=true)
        const isActiveNowA = a.is_active && !a.completed;
        const isActiveNowB = b.is_active && !b.completed;
        
        // 1. Sort by Type priority
        const typeA = typePriorityMap[a.tag_type] || 999;
        const typeB = typePriorityMap[b.tag_type] || 999;
        if (typeA !== typeB) return typeA - typeB;
        
        // 2. Sort by Domain priority
        const domainA = domainPriorityMap[a.tag_domain] || 999;
        const domainB = domainPriorityMap[b.tag_domain] || 999;
        if (domainA !== domainB) return domainA - domainB;
        
        // 3. Sort by Active Date ("Active: Now" tasks first, then by date, then tasks without dates)
        if (isActiveNowA && !isActiveNowB) return -1;
        if (!isActiveNowA && isActiveNowB) return 1;
        if (!isActiveNowA && !isActiveNowB) {
          // Neither is "Active: Now" - sort by activation_date
          if (activeDateA && !activeDateB) return -1;
          if (!activeDateA && activeDateB) return 1;
          if (activeDateA && activeDateB) {
            const activeDiff = activeDateA - activeDateB;
            if (activeDiff !== 0) return activeDiff;
          }
        }
        
        // 4. Sort by Due Date (tasks with dates before tasks without, earlier dates first)
        if (dueDateA && !dueDateB) return -1;
        if (!dueDateA && dueDateB) return 1;
        if (dueDateA && dueDateB) {
          const dueDiff = dueDateA - dueDateB;
          if (dueDiff !== 0) return dueDiff;
        }
        
        // 5. Sort by Energy priority
        const energyA = energyPriorityMap[a.tag_energy] || 999;
        const energyB = energyPriorityMap[b.tag_energy] || 999;
        return energyA - energyB;
      });
    });
    
    setGrouped(groups);
  }, [storageTasks, groupBy, tags]);

  // Build group order based on current groupBy selection
  let sortedGroups = [];
  
  if (groupBy === 'Active Date' || groupBy === 'Due Date') {
    // Sort by date: "Currently Active" first, then past dates (oldest first), then future dates (nearest first), "No Date" last
    sortedGroups = Object.keys(grouped).sort((a, b) => {
      if (a === 'Currently Active') return -1;
      if (b === 'Currently Active') return 1;
      if (a === 'No Date') return 1;
      if (b === 'No Date') return -1;
      if (a === 'No Active Date' || a === 'No Due Date') return 1;
      if (b === 'No Active Date' || b === 'No Due Date') return -1;
      
      // Parse the bucket keys
      const parseKey = (key) => {
        if (key === 'today') return { type: 'today', value: 0 };
        if (key === 'tomorrow') return { type: 'future', value: 1 };
        if (key === 'yesterday') return { type: 'past', value: 1 };
        if (key.startsWith('future-day-')) return { type: 'future', value: parseInt(key.split('-')[2]) };
        if (key.startsWith('future-week-')) return { type: 'future', value: parseInt(key.split('-')[2]) * 7 };
        if (key.startsWith('past-day-')) return { type: 'past', value: parseInt(key.split('-')[2]) };
        if (key.startsWith('past-week-')) return { type: 'past', value: parseInt(key.split('-')[2]) * 7 };
        return { type: 'unknown', value: 0 };
      };
      
      const aKey = parseKey(a);
      const bKey = parseKey(b);
      
      // Today comes first
      if (aKey.type === 'today') return -1;
      if (bKey.type === 'today') return 1;
      
      // Past dates before future dates
      if (aKey.type === 'past' && bKey.type === 'future') return -1;
      if (aKey.type === 'future' && bKey.type === 'past') return 1;
      
      // Within past dates: furthest (largest value) first
      if (aKey.type === 'past' && bKey.type === 'past') {
        return bKey.value - aKey.value;
      }
      
      // Within future dates: nearest (smallest value) first
      if (aKey.type === 'future' && bKey.type === 'future') {
        return aKey.value - bKey.value;
      }
      
      return 0;
    });
  } else {
    // Sort by tag priority
    // Handle case where groupBy is 'Assignee' but category in DB is 'assignee'
    const categoryToMatch = groupBy === 'Assignee' ? 'assignee' : groupBy;
    const categoryTags = tags.filter((t) => t.category === categoryToMatch);
    const groupOrder = categoryTags
      .sort((a, b) => a.priority - b.priority)
      .map((t) => `${t.emoji} ${t.name}`);
    
    sortedGroups = Object.keys(grouped).sort((a, b) => {
      const indexA = groupOrder.indexOf(a);
      const indexB = groupOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  const handleAddTaskForTag = (tagName) => {
    // Set the prefilled tag based on groupBy category
    const prefilledData = {
      category: groupBy,
      value: tagName
    };
    setPrefilledTag(prefilledData);
    setShowAddTask(true);
  };

  const handleTaskAdded = () => {
    setShowAddTask(false);
    setPrefilledTag(null);
    fetchTasks();
  };

  return (
    <main className="pb-6 sm:pb-8 w-full" data-testid="storage-page">
      <div className="sticky top-[60px] z-20 pt-2 md:pt-6 pb-4 mb-6 bg-slate-900">
        <header className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="max-w-[794px]">
          {/* Desktop: header and buttons */}
          <div className="hidden md:block">
            {/* Header row - title only */}
            <div className="flex items-center gap-2">
              <h1 className="h-font text-2xl md:text-3xl font-semibold text-white">Task Storage</h1>
              <HelpDialog 
                title="Task Storage Guide" 
                content={`**This page is a place to store any and every task you can think of that you need to or want to get done.** Here they can be "out of sight, out of mind" until you need to focus on them by moving them to the active list.

**All created tasks are automatically grouped and sorted in Storage.** Within each group, tasks are sorted by Type → Domain → Active Date → Due Date → Energy priority.

You can **change how tasks are grouped** by using the "<u>Group by</u>" drop-down in the upper right corner. Tasks will still maintain priority order within whichever grouping you select.

In the upper right corner, there are some **other helpful buttons**:

- <u>Hide Active</u>: This hides all active tasks from the Storage page.
- <u>Upcoming</u>: This filters all tasks to only show ones with due dates that are 14 days or less from today.
- <u>Overdue</u>: This filters all tasks to only show ones that are past due.`}
                size="large"
              />
            </div>
            
            {/* Combined filter row: Context | Group by | Upcoming | Overdue | Hide Active | Assignee */}
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <ContextSelector
                showEditButton={true}
                triggerClassName="h-8 bg-transparent border border-[var(--color-secondary)] text-[var(--color-secondary)] hover:bg-[var(--color-secondary)] hover:text-black"
                align="start"
              />
              <Select value={groupBy} onValueChange={handleGroupByChange}>
                <SelectTrigger
                  className="h-8 w-auto px-3 text-sm text-black"
                  data-testid="grouping-select"
                  style={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    backgroundColor: 'var(--color-secondary)',
                  }}
                >
                  <span>Group by: {groupBy}</span>
                </SelectTrigger>
                <SelectContent>
                  {assigneeTagsEnabled && <SelectItem value="Assignee">Assignee</SelectItem>}
                  <SelectItem value="Type">Type</SelectItem>
                  <SelectItem value="Domain">Domain</SelectItem>
                  {energyTagsEnabled && <SelectItem value="Energy">Energy</SelectItem>}
                  <SelectItem value="Active Date">Active Date</SelectItem>
                  <SelectItem value="Due Date">Due Date</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilterMode(filterMode === 'upcoming' ? 'all' : 'upcoming')}
                data-testid="upcoming-filter-button"
                style={filterMode === 'upcoming' ? {
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                } : {
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  backgroundColor: 'var(--color-secondary)',
                }}
              >
                Upcoming ({upcomingCount})
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilterMode(filterMode === 'overdue' ? 'all' : 'overdue')}
                data-testid="overdue-filter-button"
                style={filterMode === 'overdue' ? {
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                } : {
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  backgroundColor: 'var(--color-secondary)',
                }}
              >
                Overdue ({overdueCount})
              </Button>
              {storageActiveVisibility !== 'hidden' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setHideActive(!hideActive)}
                  data-testid="hide-active-button"
                  style={hideActive ? {
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                  } : {
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    backgroundColor: 'var(--color-secondary)',
                  }}
                >
                  Hide Active
                </Button>
              )}
              {assigneeTagsEnabled && assigneeTags.length > 0 && (
                <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                  <SelectTrigger
                    className="h-8 w-auto px-3 text-sm"
                    data-testid="assignee-filter-storage"
                    style={{
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      backgroundColor: 'var(--color-secondary)',
                    }}
                  >
                    <User size={14} className="mr-1.5" />
                    <span>{assigneeFilter === 'any-user' ? 'Any User' : assigneeFilter.replace(/^[^\s]+\s/, '')}</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any-user">Any User</SelectItem>
                    {assigneeTags.map((tag) => (
                      <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                        {tag.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          
          {/* Mobile: header with burger menu */}
          <div className="md:hidden">
            {/* Header row with title and burger menu */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h1 className="h-font text-2xl font-semibold text-white">Task Storage</h1>
                <HelpDialog 
                  title="Task Storage Guide" 
                  content={`**This page is a place to store any and every task you can think of that you need to or want to get done.** Here they can be "out of sight, out of mind" until you need to focus on them by moving them to the active list.

**All created tasks are automatically grouped and sorted in Storage.** Within each group, tasks are sorted by Type → Domain → Active Date → Due Date → Energy priority.

You can **change how tasks are grouped** by using the "<u>Group by</u>" drop-down in the upper right corner. Tasks will still maintain priority order within whichever grouping you select.

In the upper right corner, there are some **other helpful buttons**:

- <u>Hide Active</u>: This hides all active tasks from the Storage page.
- <u>Upcoming</u>: This filters all tasks to only show ones with due dates that are 14 days or less from today.
- <u>Overdue</u>: This filters all tasks to only show ones that are past due.`}
                  size="large"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-300 hover:text-black hover:bg-slate-200"
                data-testid="mobile-menu-toggle-storage"
              >
                <Menu size={24} />
              </Button>
            </div>
            
            {/* Collapsible menu content */}
            {mobileMenuOpen && (
              <div className="mt-3 flex flex-col gap-2 items-start">
                {/* First row: Upcoming and Overdue buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setFilterMode(filterMode === 'upcoming' ? 'all' : 'upcoming')}
                    data-testid="upcoming-filter-button-mobile"
                    style={filterMode === 'upcoming' ? { 
                      backgroundColor: 'var(--color-primary)', 
                      color: 'white', 
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    } : { 
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      backgroundColor: 'var(--color-secondary)'
                    }}
                  >
                    Upcoming ({upcomingCount})
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setFilterMode(filterMode === 'overdue' ? 'all' : 'overdue')}
                    data-testid="overdue-filter-button-mobile"
                    style={filterMode === 'overdue' ? { 
                      backgroundColor: 'var(--color-primary)', 
                      color: 'white', 
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    } : { 
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      backgroundColor: 'var(--color-secondary)'
                    }}
                  >
                    Overdue ({overdueCount})
                  </Button>
                  {storageActiveVisibility !== 'hidden' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setHideActive(!hideActive)}
                      data-testid="hide-active-button-mobile"
                      style={hideActive ? { 
                        backgroundColor: 'var(--color-primary)', 
                        color: 'white', 
                        borderWidth: '1px',
                        borderStyle: 'solid'
                      } : { 
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        backgroundColor: 'var(--color-secondary)'
                      }}
                    >
                      Hide Active
                    </Button>
                  )}
                </div>
                
                {/* Second row: Group by dropdown and Assignee filter */}
                <div className="flex flex-wrap items-center gap-2 w-full">
                  <ContextSelector
                    showEditButton={true}
                    triggerClassName="h-8 bg-transparent border border-[var(--color-secondary)] text-[var(--color-secondary)] hover:bg-[var(--color-secondary)] hover:text-black"
                    align="start"
                  />
                  <Select value={groupBy} onValueChange={handleGroupByChange}>
                    <SelectTrigger
                      className="h-8 w-auto px-3 text-sm text-black"
                      data-testid="grouping-select-mobile"
                      style={{
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        backgroundColor: 'var(--color-secondary)',
                      }}
                    >
                      <span>Group by: {groupBy}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {assigneeTagsEnabled && <SelectItem value="Assignee">Assignee</SelectItem>}
                      <SelectItem value="Type">Type</SelectItem>
                      <SelectItem value="Domain">Domain</SelectItem>
                      {energyTagsEnabled && <SelectItem value="Energy">Energy</SelectItem>}
                      <SelectItem value="Active Date">Active Date</SelectItem>
                      <SelectItem value="Due Date">Due Date</SelectItem>
                    </SelectContent>
                  </Select>
                  {assigneeTagsEnabled && assigneeTags.length > 0 && (
                    <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                      <SelectTrigger 
                        className="h-8 w-auto px-3 text-sm"
                        data-testid="assignee-filter-storage-mobile"
                        style={{
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          backgroundColor: 'var(--color-secondary)'
                        }}
                      >
                        <User size={14} className="mr-1.5" />
                        <span>{assigneeFilter === 'any-user' ? 'Any User' : assigneeFilter.replace(/^[^\s]+\s/, '')}</span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any-user">Any User</SelectItem>
                        {assigneeTags.map((tag) => (
                          <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                            {tag.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        </header>
        
        {/* Tag Navigation Bar - displayed below action buttons */}
        {navigationTags.length > 0 && (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-3">
            {/* Desktop: Show tag pills (only tags with tasks) */}
            <div className="hidden sm:flex flex-wrap gap-2">
              {navigationTags
                .filter((tag) => {
                  const tagKey = `${tag.emoji} ${tag.name}`;
                  return grouped[tagKey] && grouped[tagKey].length > 0;
                })
                .map((tag) => {
                  const tagKey = `${tag.emoji} ${tag.name}`;
                  
                  return (
                    <button
                      key={tag.id}
                      onClick={() => scrollToGroup(tagKey)}
                      className="px-3 py-1.5 rounded-full text-sm font-medium transition-all bg-slate-700/50 text-slate-200 hover:bg-white hover:text-black cursor-pointer"
                      data-testid={`tag-nav-${tag.name}`}
                    >
                      {tag.emoji} {tag.name}
                      <span className="ml-1.5 text-xs">
                        ({grouped[tagKey].length})
                      </span>
                    </button>
                  );
                })}
            </div>
            
            {/* Mobile: Show Jump to dropdown */}
            <div className="sm:hidden flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Jump to:</span>
              <Select 
                value={currentVisibleGroup || ""} 
                onValueChange={(value) => {
                  if (value) scrollToGroup(value);
                }}
              >
                <SelectTrigger className="w-auto min-w-fit" data-testid="jump-to-select-mobile">
                  <SelectValue placeholder="Select group..." />
                </SelectTrigger>
                <SelectContent>
                  {navigationTags
                    .filter((tag) => {
                      const tagKey = `${tag.emoji} ${tag.name}`;
                      return grouped[tagKey] && grouped[tagKey].length > 0;
                    })
                    .map((tag) => {
                      const tagKey = `${tag.emoji} ${tag.name}`;
                      return (
                        <SelectItem key={tag.id} value={tagKey}>
                          {tag.emoji} {tag.name} ({grouped[tagKey].length})
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Desktop gradient overlays - fade from content edges to window edges */}
      <div className="hidden md:block pointer-events-none">
        {/* Left gradient */}
        <div 
          className="fixed left-0 top-[60px] bottom-0 z-30"
          style={{
            width: `calc((100vw - 48rem) / 2)`,
            background: 'linear-gradient(to right, rgb(15 23 42) 0%, transparent 100%)'
          }}
        />
        {/* Right gradient */}
        <div 
          className="fixed right-0 top-[60px] bottom-0 z-30"
          style={{
            width: `calc((100vw - 48rem) / 2)`,
            background: 'linear-gradient(to left, rgb(15 23 42) 0%, transparent 100%)'
          }}
        />
      </div>

      {storageTasks.length === 0 && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-6">
          <div
            className="text-center py-16 px-4 rounded-2xl"
            style={{ backgroundColor: 'var(--color-secondary)' }}
            data-testid="empty-storage-state"
          >
            <p className="text-lg text-muted-foreground">
              {filterMode === 'upcoming' 
                ? 'No upcoming tasks in storage.' 
                : filterMode === 'overdue' 
                ? 'No overdue tasks in storage.' 
                : 'No tasks in storage. Create some tasks to get started!'}
            </p>
          </div>
        </div>
      )}

      {/* Groups container - horizontal or vertical based on setting */}
      <div 
        ref={groupsContainerRef}
        className={`mt-6 ${horizontalGroups ? 'storage-horizontal-scroll flex gap-4 sm:gap-6 overflow-x-auto pb-4' : 'space-y-8 max-w-3xl mx-auto px-4 sm:px-6'}`}
        style={horizontalGroups ? { 
          paddingLeft: horizontalPadding,
          paddingRight: horizontalRightPadding,
          WebkitOverflowScrolling: 'touch'
        } : {
          paddingBottom: verticalBottomPadding
        }}
      >
        {sortedGroups.map((groupName) => {
          // Determine if we should show the formatted date or tag name
          const isDateGroup = groupBy === 'Active Date' || groupBy === 'Due Date';
          let displayName = groupName;
          
          if (isDateGroup) {
            if (groupName === 'Currently Active') {
              // Show "Currently Active" as-is
              displayName = 'Currently Active';
            } else if (groupName === 'No Date') {
              // Show "No Active Date" or "No Due Date" based on grouping
              displayName = groupBy === 'Active Date' ? 'No Active Date' : 'No Due Date';
            } else {
              // Show only relative time (no full date format)
              displayName = formatDateHeader(groupName);
            }
          }
          
          // Get visible tasks for this group (pagination)
          const allGroupTasks = grouped[groupName];
          const visibleCount = getVisibleCount(groupName);
          const visibleTasks = allGroupTasks.slice(0, visibleCount);
          const hasMoreTasks = allGroupTasks.length > visibleCount;
          const remainingCount = allGroupTasks.length - visibleCount;
          
          return (
            <section 
              key={groupName}
              data-group-id={groupName}
              className={`space-y-3 ${horizontalGroups ? 'flex-shrink-0 w-[calc(100vw-5rem)] sm:w-[420px] sm:min-w-[420px] sm:max-w-[600px]' : ''}`}
            >
              <h2 className="h-font text-lg font-semibold flex items-center gap-2" data-testid={`group-${groupName}`}>
                {displayName}
                <span className="text-sm font-normal text-muted-foreground">
                  {hasMoreTasks 
                    ? `(${visibleTasks.length}/${allGroupTasks.length})`
                    : `(${allGroupTasks.length})`
                  }
                </span>
                {!isDateGroup && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleAddTaskForTag(groupName)}
                    title={`Add task with ${groupName} tag`}
                  >
                    <Plus size={16} />
                  </Button>
                )}
              </h2>
              <div className="space-y-3">
                {visibleTasks.map((task, i) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i, 10) * 0.02 }}
                  >
                    <TaskCard 
                      task={task} 
                      onComplete={fetchTasks} 
                      onUpdate={fetchTasks} 
                      tags={tags} 
                      dragHandlePosition={dragHandlePosition}
                      compact={horizontalGroups}
                    />
                  </motion.div>
                ))}
                {/* Load More button for this group */}
                {hasMoreTasks && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleLoadMoreForGroup(groupName)}
                    className="w-full mt-2"
                    data-testid={`load-more-${groupName}`}
                  >
                    {horizontalGroups 
                      ? `Load 10 More (All Groups)` 
                      : `Load More (${remainingCount} remaining)`
                    }
                  </Button>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {/* Fixed horizontal scrollbar at bottom of viewport - desktop only */}
      {horizontalGroups && scrollState.scrollWidth > scrollState.clientWidth && (
        <div 
          className="hidden sm:block fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 border-t border-slate-700/50 px-4 py-2"
          data-testid="fixed-scrollbar-container"
        >
          <div 
            ref={fixedScrollbarRef}
            className="storage-fixed-scrollbar overflow-x-auto"
            onScroll={handleFixedScrollbarScroll}
            style={{ maxWidth: '100%' }}
          >
            <div style={{ width: scrollState.scrollWidth, height: '1px' }} />
          </div>
        </div>
      )}

      <AddTaskDialog
        open={showAddTask}
        onOpenChange={setShowAddTask}
        onTaskAdded={handleTaskAdded}
        tags={tags}
        prefilledTag={prefilledTag}
      />
    </main>
  );
}
